
'use client';

import { useActionState, useEffect, useState } from 'react';
import Link from 'next/link';
import { getUserCsrfToken, userLoginAction } from '@/app/actions/auth-actions';
import { Lock, Mail } from 'lucide-react';
import Image from 'next/image';

const initialState = {
    error: '',
};

export default function LoginPage() {
    const [state, formAction, isPending] = useActionState(userLoginAction, initialState);
    const [csrfToken, setCsrfToken] = useState<string>('');
    const [callbackUrl, setCallbackUrl] = useState<string>('/');

    useEffect(() => {
        getUserCsrfToken().then(setCsrfToken);
    }, []);

    useEffect(() => {
        const url = new URL(window.location.href);
        setCallbackUrl(url.searchParams.get('callbackUrl') || '/');
    }, []);

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-[var(--color-bg-base)] text-foreground relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-800 via-zinc-950 to-zinc-950 opacity-50 z-0 pointer-events-none" />

            <div className="relative z-10 w-full max-w-md p-8 bg-[var(--color-bg-surface)] backdrop-blur-xl border border-border rounded-2xl shadow-2xl">
                <div className="text-center mb-10">
                    <div className="flex justify-center mb-6">
                        <Image
                            src="/logo/carsite.png"
                            alt="CarSite Logo"
                            width={200}
                            height={54}
                            className="h-12 w-auto object-contain light-mode-only"
                            priority
                        />
                        <Image
                            src="/logo/carsite-white.png"
                            alt="CarSite Logo"
                            width={200}
                            height={54}
                            className="h-12 w-auto object-contain dark-mode-only"
                            priority
                        />
                    </div>
                    <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
                        Sign in
                    </h1>
                    <p className="text-foreground/60 mt-2 text-sm">Sign in to like posts and manage your profile</p>
                </div>

                <form action={formAction} className="space-y-6">
                    <input type="hidden" name="csrf_token" value={csrfToken} />
                    <input type="hidden" name="callbackUrl" value={callbackUrl} />
                    <div className="space-y-2">
                        <label htmlFor="identifier" className="text-xs font-medium text-foreground/60 ml-1 uppercase tracking-wider">Email or username</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-foreground/60 group-focus-within:text-emerald-500 transition-colors">
                                <Mail size={18} />
                            </div>
                            <input
                                id="identifier"
                                type="text"
                                name="identifier"
                                autoComplete="username"
                                required
                                className="w-full pl-10 pr-4 py-3 bg-[var(--color-bg-base)] border border-[var(--color-border-input)] rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all outline-none text-foreground placeholder-foreground/60"
                                placeholder="you@example.com or @yourhandle"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="password" className="text-xs font-medium text-foreground/60 ml-1 uppercase tracking-wider">Password</label>
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-foreground/60 group-focus-within:text-emerald-500 transition-colors">
                                <Lock size={18} />
                            </div>
                            <input
                                id="password"
                                type="password"
                                name="password"
                                autoComplete="current-password"
                                required
                                className="w-full pl-10 pr-4 py-3 bg-[var(--color-bg-base)] border border-[var(--color-border-input)] rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all outline-none text-foreground placeholder-foreground/60"
                                placeholder="Enter your password"
                            />
                        </div>
                    </div>

                    {state?.error && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center">
                            {state.error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isPending || !csrfToken}
                        className="w-full py-3.5 px-4 bg-gradient-to-r from-primary to-primary/60 hover:from-primary hover:to-primar/80 text-white font-medium rounded-xl shadow-lg hover:shadow-primary/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>

                <div className="mt-6 text-center text-xs text-foreground/60">
                    <p>
                        New here?{' '}
                        <Link href={`/register?callbackUrl=${encodeURIComponent(callbackUrl)}`} className="text-primary hover:text-primary/80 underline underline-offset-4">
                            Create an account
                        </Link>
                    </p>
                    <p className="mt-2">
                        <Link href="/reset-password" className="text-primary hover:text-primary/80 underline underline-offset-4">
                            Forgot your password?
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
